/**
 * Turning a recording into something a therapist can see.
 *
 * A take is a few minutes of their own voice, and the only questions they ask
 * of it are answered by its shape: did it record at all, where does the long
 * silence sit, is the ending clipped. So the studio draws the take rather than
 * offering a bare play button — the same reason an audio editor does.
 */

/** How many bars the studio draws. Enough to show a pause, few enough to read. */
export const WAVEFORM_BUCKETS = 96;

/**
 * The peak level of each slice of a recording, scaled so the loudest bar fills
 * the lane.
 *
 * Peaks rather than averages: a whispered meditation averages close to
 * silence, and a waveform that renders it as a flat line says the recording
 * failed when it did not. Normalising against the loudest moment is what makes
 * a quiet take legible at all — the lane says where the sound is, never how
 * many decibels it was.
 */
export function peaksFromSamples(
    samples: ArrayLike<number>,
    buckets: number = WAVEFORM_BUCKETS
): number[] {
    if (buckets <= 0 || samples.length === 0) return [];

    const size = samples.length / buckets;
    const peaks: number[] = [];

    for (let bucket = 0; bucket < buckets; bucket += 1) {
        const start = Math.floor(bucket * size);
        const end = Math.min(Math.floor((bucket + 1) * size), samples.length);
        let peak = 0;
        for (let index = start; index < end; index += 1) {
            const level = Math.abs(samples[index]);
            if (level > peak) peak = level;
        }
        peaks.push(peak);
    }

    const loudest = Math.max(...peaks);
    // A take of pure silence stays flat rather than being scaled up into a
    // wall of noise that is not there.
    if (loudest === 0) return peaks;
    return peaks.map((peak) => peak / loudest);
}

/** The same, from a decoded buffer. Channel 0 is enough to draw a shape. */
export function peaksFromBuffer(
    buffer: { getChannelData: (channel: number) => Float32Array },
    buckets: number = WAVEFORM_BUCKETS
): number[] {
    return peaksFromSamples(buffer.getChannelData(0), buckets);
}
